import React, {useCallback, useEffect, useMemo} from 'react';
import {Box, Button, IconButton, Tooltip} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import styles from './cost-report-top-bar.module.scss'
import moment from 'moment';
import { Year } from '../../../../features/settings/workcalendar/types';
import { useAppSelector } from '../../../../hooks/hooks';
import { updateTimeTrackingDateRangeFilter } from '../../../../features/time-tracking-filters';
import classNames from 'classnames';
import { SVGS } from '../../../../assets/images';
import { Lx } from '../../../../i18n/consts';
import { useMediaQuery } from 'react-responsive';
import { RangeType } from '../../../../components/shared/calendar/types';
import ScheduleDateSelector from '../../../../components/Schedule/header/components/schedule-date-selector';
import { getDefaultMonthRange, getSelectedDateRangeFromFilter, mapWorkCalendarYears } from '../../../../components/shared/calendar/utils';

const CostReportTitle = ({ className }: { className: string }) => (
    <div className={styles.titleWithSpinner}>
        <div className={className}>
            <FormattedMessage id={'costreport'} />
        </div>
    </div>
);

interface Props {
    years: Year[];
    today: moment.Moment;
    onCreateCostClick: () => void;
    onMobileFilterToggle?: () => void;
    mobileFiltersOpen?: boolean;
    hasActiveFilters?: boolean;
    disabled?: boolean;
}

export const CostReportTopBar = ({
    years,
    today,
    onCreateCostClick,
    onMobileFilterToggle,
    mobileFiltersOpen,
    hasActiveFilters,
    disabled,
}: Props) => {
    const intl = useIntl();
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    if (isMobile) {
        return (
            <div className={classNames(styles.container, styles.mobileContainer)}>
                <div className={styles.mobileHeaderRow}>
                    <CostReportTitle className={styles.mobileTitle} />
                    <div className={styles.mobileHeaderActions}>
                        <IconButton
                            aria-label='filterMobile'
                            className={classNames(styles.mobileIconButton, {
                                [styles.mobileIconButtonActive]: hasActiveFilters || mobileFiltersOpen,
                                [styles.mobileIconButtonOpen]: mobileFiltersOpen,
                            })}
                            onClick={onMobileFilterToggle}
                            disabled={disabled}>
                            <SVGS.FilterAltRounded />
                        </IconButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Box className={styles.header}>
                <div className={classNames({
                    [styles.headerItem]: true,
                    [styles.dateSelector]: true
                })}>
                    <CostReportTitle className={styles.headerText} />
                </div>
                <Box className={classNames(styles.dateSelector, styles.headerCalendar)}>
                    <CostReportDateSelector
                        years={years}
                        today={today}
                        disabled={disabled}
                    />
                </Box>
                <Box gap={1} display={'flex'} height={'100%'} className={styles.headerItem}>
                    <Tooltip title={intl.formatMessage({ id: Lx.Cost.ADD })}>
                        <span>
                            <Button className={styles.actionButton} sx={{ height: '100%' }} onClick={onCreateCostClick} disabled={disabled}>
                                <SVGS.PlusIcon className={styles.icon} />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </div>
    )};

interface CalendarPopupState {
    open: boolean;
    setOpen: (value: boolean) => void;
}

interface CostReportDateSelectorProps {
    years: Year[];
    today: moment.Moment;
    calendarState?: CalendarPopupState;
    disabled?: boolean;
}

export const CostReportDateSelector: React.FC<CostReportDateSelectorProps> = ({
    years,
    today,
    calendarState,
    disabled,
}) => {
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.dateRangeFilter);
    const defaultMonthRange = useMemo(() => getDefaultMonthRange(today), [today]);
    const allowedRangeTypes = [
        RangeType.CurrentMonth,
        RangeType.PreviousMonth,
        RangeType.CurrentQuarter,
        RangeType.PreviousQuarter,
        RangeType.CurrentYear,
        RangeType.PreviousYear,
    ];

    const handleDateRangeChange = useCallback((values: moment.Moment[]): Promise<void> => {
        updateTimeTrackingDateRangeFilter({
            startDate: values[0].format('YYYY-MM-DD'),
            endDate: values[1].format('YYYY-MM-DD'),
        });
        return Promise.resolve();
    }, []);

    const selectedDates = useMemo(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            return [defaultMonthRange.startDate, defaultMonthRange.endDate];
        }

        return getSelectedDateRangeFromFilter(dateRangeFilter, defaultMonthRange);
    }, [dateRangeFilter, defaultMonthRange]);

    useEffect(() => {
        if (dateRangeFilter?.startDate && dateRangeFilter?.endDate) {
            return;
        }

        void handleDateRangeChange([defaultMonthRange.startDate, defaultMonthRange.endDate]);
    }, [dateRangeFilter?.endDate, dateRangeFilter?.startDate, defaultMonthRange, handleDateRangeChange]);

    return (
        <ScheduleDateSelector
            selectedDates={selectedDates}
            onRangeSelected={handleDateRangeChange}
            years={mapWorkCalendarYears(years)}
            minDate={today.clone().add(-365, 'days')}
            allowedRangeTypes={allowedRangeTypes}
            calendarState={calendarState}
            disabled={disabled}
        />
    );
};
